/**
 * THE LAST LOOK — the screen a survey passes through before it can be sent
 * (**D-017**), and the screen it can now be FINISHED on (**D-023**).
 *
 * The design authority, walkthrough round 3, 2026-08-06:
 *
 *   *"I just closed and submitted an incomplete survey… I should not have been
 *    able to submit with unanswered questions."*
 *
 * and, live on production, 2026-08-07, standing on the screen that refusal
 * produced:
 *
 *   *"On the final screen it says five answers are still outstanding… when I tap
 *    the answer button it just takes me back to the original screen."*
 *   *"It sure would be nice if I could answer all the missing questions right
 *    here on the last 'Look Before You Send' screen… That way I don't have to
 *    navigate all the way back through all the screens."*
 *
 * `src/ui/state/reckoning.ts` decides what is on this screen and why; this
 * renders it — and, since D-023, lets the surveyor answer it here.
 *
 * ── Why the controls are the SECTION SCREEN'S controls ───────────────────────
 * Each row renders `QuestionList` narrowed to the questions that row is about.
 * Not a second renderer, not a copy of the control, not a second write path: the
 * scope narrowing, the persistence, the D-016 provenance and the §4 arithmetic
 * are the ones the section screen uses, byte for byte. A second implementation
 * is precisely how this screen and the section screen would come to disagree
 * about the same answer — the class of defect this whole round is about.
 *
 * The one thing the inline view deliberately does NOT inherit is D-016's
 * defaulting. See the note in `QuestionList`: a row exists because an answer is
 * outstanding, and a screen that answered it by being scrolled past would be the
 * silent submission in a third costume.
 *
 * ── A rule is a PAIR, and it is rendered as one ──────────────────────────────
 * §4 rules 2 and 3 span replace AND refurbish. `OutstandingItem.questionIds`
 * carries both, so the row shows both controls under ONE running total —
 * *"Cab light disconnects: 1 of 2 cars accounted for"*, live, printed by
 * `headlineFor` in results.ts so the sentence is the same one the section header
 * uses. Fixing one number alone can therefore never leave the other half
 * stranded somewhere the surveyor has to go and find.
 *
 * ── Nothing vanishes from under a thumb ─────────────────────────────────────
 * A rule row leaves the reckoning the moment the surveyor answers the first of
 * its questions themselves — deliberately, because §2.3 leaves a number somebody
 * typed on purpose with Chip and Tim rather than demanding an acknowledgement
 * for it (see reckoning.ts). On a screen you navigate AWAY from, that is
 * invisible. On a screen you TYPE into, it would be the row disappearing between
 * the first stepper tap and the second.
 *
 * So every row this screen has shown stays on it for the rest of the visit,
 * reading its state off the LIVE blocker: still open, being worked on, or
 * settled. Nothing about the submit gate changes — `reckoning.settled` is still
 * the only thing that decides — these rows simply do not evaporate mid-edit.
 *
 * ── It is the end of the walk, not an error screen ───────────────────────────
 * Nothing here is red until the surveyor makes it red. There is no alarm word,
 * no "invalid", no count of everything wrong, and no disabled Submit sitting at
 * the bottom taunting them — Submit does not exist on this screen at all
 * (D-014/D-015: an absent control is calm, a dead one is a puzzle). The heading
 * says how much of the job is left, the rows say what, and each row offers the
 * one thing a surveyor can do about it: **answer it, right here**.
 *
 * The row's own controls are the whole of its surface since D-033 (R7: the
 * map is the way to a section; the link that stood here is gone).
 *
 * ── The screen is the questions — round 7, D-038 ─────────────────────────────
 * The design authority, 2026-09-03:
 *
 *   *"De-noise the screen by removing unnecessary helper text and repeated
 *    labels (e.g. 'Is any car getting new cab flooring' appearing more than
 *    once)."*
 *   *"Remove noise such as [the empty-field sentence], the note [that a
 *    question still needs an answer], and the blocker entry option [the
 *    drawer's toggle]."* — quoted with its three examples paraphrased, because
 *    `tests/unit/word-diet.test.ts` bans those sentences from src/ui verbatim,
 *    comments included, and a header that quoted them would be the words back
 *    in the file that just deleted them.
 *   *"Make the screen simple and consistent in appearance, matching the design
 *    of the other screens."*
 *
 * Three things left, and each of them was the screen saying in prose what the
 * control beneath it already showed:
 *
 *   the **kind message** — a row whose only question is an empty box does not
 *   need a sentence saying the box is empty;
 *   the **resolution line** — the tally chip above it already names the target;
 *   the **blocker drawer** — a toggle, a reason field and a confirm that marked
 *   the answer unavailable: a second way to settle a row without answering it,
 *   and a second vocabulary to learn at the door.
 *
 * §2.3's escape did not close with the drawer. A question nobody could get is
 * answered No / 0 and the blockage goes in that section's notes (D-033 R15) —
 * the door every other screen already has, and now the only one. See
 * reckoning.ts.
 *
 * And the repeated label: a row that holds ONE question whose catalog label IS
 * the row title prints no title of its own, because `QuestionField` renders
 * that label as the field's own heading a few pixels below. `flagCount` and
 * `bool` are not in `SELF_LABELLING`, so it always did — the row was simply
 * saying it first. The comparison is made HERE and `QuestionField` is not
 * touched: the section screens want that heading exactly as it is.
 */

import { Fragment, useRef, useState, type ReactNode } from 'react';

import { byId } from '../../core/catalog';
import type { AnswerValue } from '../../core/types';
import { hrefFor } from '../routing';
import { roomCountPlan } from '../state/buildingSetup';
import {
  blockersFor,
  headlineFor,
  type SectionBlocker,
  type SurveyResults,
} from '../state/results';
import { lastLookSummary, type OutstandingItem, type Reckoning } from '../state/reckoning';
import { useSurvey } from '../state/SurveyProvider';
import { MathChip } from './Chips';
import { ShotReasonField } from './NotesField';
import { QuestionList, type GuardVerdict } from './QuestionList';
import { Eyebrow } from './Surfaces';

export interface LastLookProps {
  reckoning: Reckoning;
}

/* ═══════════════════════════ what a row is doing ═════════════════════════ */

/**
 * Three states, and the two on the end are the reason this screen keeps rows it
 * is no longer being asked to demand.
 *
 *   open      something on this row is still outstanding, still withholding
 *             Submit
 *   working   nothing is demanded any more, but a §4 rule over these questions
 *             is still failing. §2.3 leaves a number somebody typed on purpose
 *             with Chip and Tim — it is not asked for twice — and the row keeps
 *             counting so it can be finished
 *   settled   nothing blocks these questions at all
 *
 * Answering is the only thing that moves a row off `open` (D-038).
 * `tests/e2e/inline-answers.spec.ts` pins all three by name.
 */
type RowState = 'open' | 'working' | 'settled';

/**
 * ONE ROW PER SET OF QUESTIONS, never one per gap.
 *
 * The distinction matters because a gap is not stable under editing and the
 * questions are. Tap "Yes" on *"Are any mainline disconnects being replaced?"*
 * and, for as long as the quantity is still 0, the document holds a DIFFERENT
 * gap — *"answered Yes, but the quantity is still 0"* — over the same two
 * questions the §4 rule 2 gap was about. Keyed by gap, that is a second row
 * appearing under a second copy of the section heading, saying the same thing in
 * other words, while the first row claims to be finished. Keyed by questions, it
 * is what it actually is: one row, now with one more reason on it.
 *
 * So a row owns a SET of question ids, absorbs any item or blocker that touches
 * them, and states every live reason it is still open. Its state is read off the
 * live blockers, never off whether a particular gap id survived.
 */
interface LastLookRowModel {
  /** React key: the first gap id this row was ever built from. Stable. */
  key: string;
  sectionInstanceId: string;
  sectionTitle: string;
  /** Every question this row answers. Grows as items are absorbed. */
  questionIds: string[];
  /** The item whose words the row leads with — the widest one it has. */
  primary: OutstandingItem;
  /** Reckoning items on this row that still withhold Submit. */
  open: OutstandingItem[];
  /** Every live blocker touching these questions — the row's reasons, now. */
  live: SectionBlocker[];
  state: RowState;
}

interface RowGroup {
  sectionInstanceId: string;
  sectionTitle: string;
  rows: LastLookRowModel[];
}

export function LastLook({ reckoning }: LastLookProps): ReactNode {
  const { results } = useSurvey();
  const groups = useStickyRows(reckoning, results);

  return (
    <>
      <Eyebrow>Last look before you send</Eyebrow>
      <div className="card">
        <p className="q" data-legibility="question">
          {lastLookSummary(reckoning)}
        </p>

      </div>


      {groups.map((group) => (
        <Fragment key={group.sectionInstanceId}>
          {/* The count is the number still OPEN in this section — a row the
           * surveyor has already dealt with is not something left to do, and a
           * heading reading "· 0" over rows that are finished would be the same
           * misleading arithmetic the section header used to print. */}
          <Eyebrow>
            {group.sectionTitle}
            {openIn(group) > 0 ? ` · ${String(openIn(group))}` : ''}
          </Eyebrow>
          {group.rows.map((row) => (
            <LastLookRow key={row.key} row={row} />
          ))}
        </Fragment>
      ))}
    </>
  );
}

/** How many rows in one section still withhold Submit. */
function openIn(group: RowGroup): number {
  return group.rows.filter((row) => row.state === 'open').length;
}

/* ══════════════════════ the rows, and why they persist ═══════════════════ */

/** What one sticky row remembers between renders. Identity, not state. */
interface StickyRow {
  key: string;
  sectionInstanceId: string;
  sectionTitle: string;
  questionIds: string[];
  /** The widest item this row has been built from — whose words it leads with. */
  primary: OutstandingItem;
}

function touches(a: readonly string[], b: readonly string[]): boolean {
  return a.some((id) => b.includes(id));
}

/**
 * The item a row LEADS with — and it never narrows under the surveyor's thumb.
 *
 * ── The defect this exists to stop (caught by the D-023 E2E, 2026-08-07) ─────
 *
 * This used to be `open[0] ?? row.primary`, and that quietly broke the row's own
 * documented contract the instant the pair was touched. Tap "Yes" on *"cab light
 * disconnects being replaced"* and, until the quantity leaves 0, the document
 * holds a NARROW follow-up gap over E5a alone (*"answered Yes, but the quantity
 * is still 0"*). Taking `open[0]` handed the whole row over to that follow-up:
 *
 *   - the heading stopped being **"Cab light disconnects"** and became one half
 *     of the pair, so the rule she was in the middle of satisfying lost its name;
 *   - the running total **"Cab light disconnects: 0 of 2 cars accounted for"**
 *     disappeared — because the reason filter drops a blocker that merely
 *     restates the heading, and the heading had just become that blocker;
 *   - and the row printed the generic "nothing is here yet" line over a control
 *     she had just entered something into. (That line is gone outright since
 *     D-038; the two above are still this function's to prevent.)
 *
 * Three sentences, all wrong, all arriving at the exact moment she was typing
 * the number they were about. That is D-021's disease — the app knowing and
 * saying the wrong thing — reappearing inside D-023's fix for it, and it is
 * precisely what D-023 §3 means by *nothing evaporates under a thumb*.
 *
 * So the row keeps the WIDEST statement it has. A rule outranks one of its own
 * follow-ups, because the rule is what the pair is about and the follow-up is
 * one of its reasons — the same rule `useStickyRows` already applies when it
 * decides which item a row leads with. Ties go to the live open item, so an
 * ordinary one-question row still speaks with its current voice.
 *
 * Display only.
 */
export function leadingItem(
  sticky: OutstandingItem,
  open: OutstandingItem | undefined,
): OutstandingItem {
  if (!open) return sticky;
  return open.questionIds.length >= sticky.questionIds.length ? open : sticky;
}

/**
 * Every row this visit has shown, grouped by section in the order they arrived.
 *
 * The refs are updated during render, which is safe because the update is
 * idempotent — a second pass (StrictMode) finds every row already recorded and
 * unions in nothing new. Same pattern, and same reason, as `useRevealed` in
 * QuestionList.
 *
 * Two things happen here and nothing else does:
 *
 *   1. **Rows are remembered.** A row that leaves the reckoning — because the
 *      surveyor answered part of it and §2.3 stopped demanding the rest — stays
 *      on screen. Without this it would disappear between the first stepper tap
 *      and the second, which on a screen you TYPE into is the app going quiet
 *      at the exact moment it has something to say.
 *   2. **Rows absorb rather than multiply.** An item whose questions a row
 *      already owns joins that row. See the note on `LastLookRowModel`.
 *
 * Nothing about the submit gate changes: `reckoning.settled` is still the only
 * thing that decides, and it is computed upstream from the engines' own gaps.
 */
function useStickyRows(reckoning: Reckoning, results: SurveyResults): RowGroup[] {
  const rowsRef = useRef<StickyRow[]>([]);

  const findRow = (item: OutstandingItem): StickyRow | undefined =>
    rowsRef.current.find((row) => {
      if (row.sectionInstanceId !== item.sectionInstanceId) return false;
      // A photo item (Amendment 8) carries no question at all — `questionIds`
      // is always `[]` on both sides, so `touches` is vacuously false and
      // this item's own row could never be found again on the NEXT render.
      // Every re-render would then push a fresh duplicate row for the same
      // shot (caught writing a reason: each keystroke's re-render minted
      // another row, and the last look showed the same shot three times
      // over). Matched by `shotListId` instead — the id a photo row actually
      // carries, and never absorbed into a question row or vice versa.
      if (item.kind === 'photo') return row.primary.kind === 'photo' && row.primary.shotListId === item.shotListId;
      return row.primary.kind !== 'photo' && touches(row.questionIds, item.questionIds);
    });

  for (const item of reckoning.items) {
    const existing = findRow(item);
    if (!existing) {
      rowsRef.current.push({
        key: item.gapId,
        sectionInstanceId: item.sectionInstanceId,
        sectionTitle: item.sectionTitle,
        questionIds: [...item.questionIds],
        primary: item,
      });
      continue;
    }
    for (const questionId of item.questionIds) {
      if (!existing.questionIds.includes(questionId)) existing.questionIds.push(questionId);
    }
    // The row leads with the WIDEST statement it has: "Cab light disconnects"
    // over "Are any cab light 110 disconnects being replaced?" — the rule is
    // what the pair is about, and the follow-up is one of its reasons.
    if (item.questionIds.length >= existing.primary.questionIds.length) {
      existing.primary = item;
    }
  }

  const groups: RowGroup[] = [];

  for (const row of rowsRef.current) {
    /*
     * A photo row (Amendment 8, D-041) carries no question at all —
     * `questionIds` is always `[]`, because there is nothing to answer, only
     * something to take or a reason to write. `touches` compares question
     * ids, so it is vacuously false for one no matter what: every photo row
     * would read `settled` from the moment it was born (found reviewing Task
     * 4). It is matched by `shotListId` instead — the id the row actually
     * carries.
     */
    const isPhoto = row.primary.kind === 'photo';
    const open = reckoning.open.filter((item) =>
      isPhoto
        ? item.sectionInstanceId === row.sectionInstanceId &&
          item.kind === 'photo' &&
          item.shotListId === row.primary.shotListId
        : item.sectionInstanceId === row.sectionInstanceId &&
          touches(item.questionIds, row.questionIds),
    );
    // The reasons, LIVE — read off the same blockers the section header and the
    // hall indicator read, never off whichever gap id happened to survive.
    const live = isPhoto
      ? blockersFor(results, row.sectionInstanceId).filter(
          (blocker) => blocker.kind === 'photo' && blocker.shotListId === row.primary.shotListId,
        )
      : blockersFor(results, row.sectionInstanceId).filter((blocker) =>
          touches(blocker.questionIds, row.questionIds),
        );

    let state: RowState;
    if (open.length > 0) state = 'open';
    else if (live.length > 0) state = 'working';
    else state = 'settled';

    const model: LastLookRowModel = {
      key: row.key,
      sectionInstanceId: row.sectionInstanceId,
      sectionTitle: row.sectionTitle,
      questionIds: row.questionIds,
      // The widest statement this row has — NEVER narrowed to a follow-up while
      // the surveyor is mid-edit. See `leadingItem`.
      primary: leadingItem(row.primary, open[0]),
      open,
      live,
      state,
    };

    const group = groups.find(
      (candidate) => candidate.sectionInstanceId === row.sectionInstanceId,
    );
    if (group) group.rows.push(model);
    else
      groups.push({
        sectionInstanceId: row.sectionInstanceId,
        sectionTitle: row.sectionTitle,
        rows: [model],
      });
  }

  return groups;
}

/* ═══════════════════════════════ one row ═════════════════════════════════ */

function LastLookRow({ row }: { row: LastLookRowModel }): ReactNode {
  const { state, primary } = row;

  /*
   * A required photo (Amendment 8, D-041) has no control to render inline —
   * there is no answer, only a picture or a reason for its absence — so this
   * row is its own shape: the shot's name, the door to go take it (a real
   * anchor, built by `hrefFor`, not a button — D-023's "navigation is
   * anchors"), and the one line that speaks for a shot not taken.
   */
  if (primary.kind === 'photo') {
    // `shotListId` is optional on the shared type (it is set only on a
    // `'photo'` row, not narrowed by `kind` itself) — a guard, not an
    // assertion, so a row that somehow arrived without one is dropped rather
    // than trusted (D-041 postscript).
    if (!primary.shotListId) return null;
    return (
      <div
        className="card lastlook lastlook--photo"
        data-testid="lastlook-item"
        data-row-state={state}
        data-row-kind="photo"
      >
        <p className="q" data-legibility="question">
          {primary.title}
        </p>
        <a
          // `btn--ghost` is this app's actual secondary-button modifier
          // (`btn--secondary` is defined nowhere in theme.css); the brief's
          // sample name is followed in spirit, not literally, so the door
          // reads like every other secondary control in the app.
          className="btn btn--ghost"
          href={hrefFor(primary.route)}
          aria-label={`Take it — ${primary.title}`}
          data-legibility="display"
        >
          Take it
        </a>
        <ShotReasonField
          sectionInstanceId={row.sectionInstanceId}
          shotListId={primary.shotListId}
          shotLabel={primary.title}
        />
      </div>
    );
  }

  /*
   * The reasons, in the engine's own words, one line each.
   *
   * `headlineFor` is results.ts's own — the very sentence the section header
   * prints — so "Cab light disconnects: 1 of 2 cars accounted for" cannot say
   * one thing here and another there. Read off the LIVE blockers, so the running
   * total keeps moving under the surveyor's thumb, including after §2.3 has
   * stopped demanding it.
   *
   * A blocker that only restates the row's own heading is dropped: "Street
   * address" over a chip reading "Street address — not answered yet" over an
   * empty box is the same fact three times, and on a 430px screen in a dim
   * machine room three lines are what stops the fourth being read.
   */
  const reasons = row.live
    .filter(
      (blocker) => blocker.tally !== undefined || !blocker.headline.startsWith(primary.title),
    )
    .map((blocker) => ({
      key: blocker.gapId,
      text: headlineFor(blocker.tally, blocker.headline),
    }));

  const heading = titleOf(row);

  return (
    <div className="card lastlook" data-testid="lastlook-item" data-row-state={state}>
      {heading === undefined ? null : (
        <p className="q" data-legibility="question">
          {heading}
        </p>
      )}

      {/* D-051: an unanswered row whose gap says more than "not answered" —
       * a ZIP with some digits in it — prints what it needs, once, without
       * repeating the label the field already carries. */}
      {state !== 'settled' && primary.kind === 'unanswered' && primary.detail !== 'not answered.' ? (
        <MathChip tone="info">{primary.detail.charAt(0).toUpperCase() + primary.detail.slice(1)}</MathChip>
      ) : null}

      {state === 'settled'
        ? null
        : reasons.map((reason) => (
            <MathChip key={reason.key} tone="info">
              {reason.text}
            </MathChip>
          ))}

      {/* The answer itself, in place — the whole point of D-023. */}
      <InlineAnswer
        sectionInstanceId={row.sectionInstanceId}
        questionIds={row.questionIds}
      />
    </div>
  );
}

/**
 * The row's own heading — or NOTHING, when the field below already is it.
 *
 * *"…and repeated labels (e.g. 'Is any car getting new cab flooring' appearing
 * more than once)."* — the design authority, 2026-09-03, D-038.
 *
 * A one-question row whose title IS that question's catalog label was printing
 * the label twice: once here, and once as `QuestionField`'s own heading, which
 * every kind outside `SELF_LABELLING` renders. The fix belongs on this side of
 * the boundary. `QuestionField` is right to print the label — a section screen
 * is nothing but those headings — so what is wrong is this screen saying it
 * first, and only this screen can tell that it has.
 *
 * A RULE row keeps its title (*"Cab light disconnects"* over two controls named
 * *"…being replaced"* and *"…being kept and refurbished"*), and so does a row
 * whose title the engine wrote itself: neither is a repeat of anything.
 */
function titleOf(row: LastLookRowModel): string | undefined {
  const title = row.primary.title;
  if (row.questionIds.length !== 1) return title;
  return byId(row.questionIds[0] ?? '')?.label === title ? undefined : title;
}

/* ════════════════════════ the controls, in place ═════════════════════════ */

/**
 * The real question controls for one row, rendered by the section screen's own
 * renderer.
 *
 * `chips={false}` because the ROW above already states the rule, its running
 * total and its target: a chip under the first of the two controls would be the
 * same sentence twice on a 430px screen, and §4 rules 2 and 3 are about the
 * pair, not about either half of it.
 *
 * `bare` because the per-question note-and-camera drawer belongs on the section
 * screen. This screen is the last thirty seconds of a day's work; the one thing
 * it must do is get the answer in.
 */
function InlineAnswer({
  sectionInstanceId,
  questionIds,
}: {
  sectionInstanceId: string;
  questionIds: readonly string[];
}): ReactNode {
  const { doc, results } = useSurvey();
  const [held, setHeld] = useState<string | null>(null);

  const section = results.sectionById.get(sectionInstanceId);
  if (!section || questionIds.length === 0) return null;

  /*
   * B12 — "How many machine rooms?" — is the one answer on this screen that can
   * DESTROY work: the room slots derive from it, so lowering it past a room that
   * holds a survey discards that survey. The building screen asks first
   * (`roomCountPlan`, D-014) and this screen has no room for that conversation,
   * so the destructive case is refused HERE, by name, with the place it can be
   * done. Everything else falls straight through to the ordinary write.
   */
  const guard = (questionId: string, next: AnswerValue): GuardVerdict => {
    if (questionId !== 'B12' || next.kind !== 'number') return 'write';
    const wanted = Math.max(0, Math.floor(next.value));
    if (wanted >= doc.rooms.length || !roomCountPlan(doc, wanted).destructive) {
      setHeld(null);
      return 'write';
    }
    setHeld(
      `Lowering this to ${String(wanted)} would remove a machine room survey that already ` +
        'holds work. Open the Building screen to do that — it asks you there first.',
    );
    return 'refused';
  };

  return (
    <div className="lastlook__answer">
      <QuestionList section={section} only={questionIds} bare chips={false} guard={guard} />
      {held ? (
        <p className="ruleline" data-legibility="helper">
          {held}
        </p>
      ) : null}
    </div>
  );
}
